import { Controller } from "@hotwired/stimulus";
import Cropper from "cropperjs";

// Connects to data-controller="avatar-menu"
export default class extends Controller {
  static targets = [
    "trigger",
    "menu",
    "lightbox",
    "lightboxDialog",
    "fileInput",
    "form",
    "cropperModal",
    "cropperDialog",
    "cropperImage",
  ];

  static LEAVE_FALLBACK_MS = 400;

  connect() {
    this.closeMenuOnOutside = this.closeMenuOnOutside.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.cropper = null;
    this.objectUrl = null;
  }

  disconnect() {
    document.removeEventListener("click", this.closeMenuOnOutside);
    document.removeEventListener("keydown", this.handleKeydown);
    this.#destroyCropper();
  }

  toggleMenu(event) {
    event.stopPropagation();
    const isOpen = this.element.classList.toggle("is-open");
    this.triggerTarget.setAttribute("aria-expanded", String(isOpen));

    if (isOpen) {
      document.addEventListener("click", this.closeMenuOnOutside);
      document.addEventListener("keydown", this.handleKeydown);
    } else {
      this.#removeMenuListeners();
    }
  }

  closeMenu() {
    this.element.classList.remove("is-open");
    this.triggerTarget.setAttribute("aria-expanded", "false");
    this.#removeMenuListeners();
  }

  closeMenuOnOutside(event) {
    if (this.element.contains(event.target)) return;
    this.closeMenu();
  }

  handleKeydown(event) {
    if (event.key !== "Escape") return;
    if (this.#cropperOpen()) {
      this.cancelCrop();
    } else if (this.#lightboxOpen()) {
      this.closeLightbox();
    } else {
      this.closeMenu();
    }
  }

  openLightbox() {
    this.closeMenu();
    this.lightboxTarget.classList.replace("hidden", "flex");
    this.#playEnterAnimation(this.lightboxTarget, this.lightboxDialogTarget);
    document.addEventListener("keydown", this.handleKeydown);
  }

  closeLightbox() {
    this.#playLeaveAnimation(
      this.lightboxTarget,
      this.lightboxDialogTarget,
      () => {
        this.lightboxTarget.classList.replace("flex", "hidden");
        if (!this.element.classList.contains("is-open") && !this.#cropperOpen()) {
          document.removeEventListener("keydown", this.handleKeydown);
        }
      },
    );
  }

  closeLightboxOnBackdrop(event) {
    if (event.target === event.currentTarget) this.closeLightbox();
  }

  pickFile() {
    this.closeMenu();
    this.fileInputTarget.value = "";
    this.fileInputTarget.click();
  }

  openCropper() {
    const file = this.fileInputTarget.files[0];
    if (!file) return;

    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = URL.createObjectURL(file);
    this.cropperImageTarget.src = this.objectUrl;

    this.cropperModalTarget.classList.replace("hidden", "flex");
    this.#playEnterAnimation(this.cropperModalTarget, this.cropperDialogTarget);
    document.addEventListener("keydown", this.handleKeydown);

    this.#destroyCropper();
    this.cropper = new Cropper(this.cropperImageTarget, {
      aspectRatio: 1,
      viewMode: 1,
      dragMode: "move",
      autoCropArea: 1,
      background: false,
      movable: true,
      zoomable: true,
      scalable: false,
      rotatable: true,
      cropBoxResizable: true,
      cropBoxMovable: true,
      responsive: true,
      restore: false,
    });
  }

  rotateLeft() {
    if (this.cropper) this.cropper.rotate(-90);
  }

  rotateRight() {
    if (this.cropper) this.cropper.rotate(90);
  }

  zoomIn() {
    if (this.cropper) this.cropper.zoom(0.1);
  }

  zoomOut() {
    if (this.cropper) this.cropper.zoom(-0.1);
  }

  reset() {
    if (this.cropper) this.cropper.reset();
  }

  cancelCrop() {
    this.#closeCropper();
    this.fileInputTarget.value = "";
  }

  cancelCropOnBackdrop(event) {
    if (event.target === event.currentTarget) this.cancelCrop();
  }

  saveCrop() {
    if (!this.cropper) return;

    const originalFile = this.fileInputTarget.files[0];
    const fileName = originalFile?.name || "avatar.png";
    const mimeType =
      originalFile?.type && originalFile.type.startsWith("image/")
        ? originalFile.type
        : "image/png";

    const canvas = this.cropper.getCroppedCanvas({
      width: 512,
      height: 512,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high",
    });

    if (!canvas) return;

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const croppedFile = new File([blob], fileName, {
          type: mimeType,
          lastModified: Date.now(),
        });
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(croppedFile);
        this.fileInputTarget.files = dataTransfer.files;
        this.#closeCropper();
        this.formTarget.requestSubmit();
      },
      mimeType,
      0.92,
    );
  }

  #closeCropper() {
    this.#playLeaveAnimation(
      this.cropperModalTarget,
      this.cropperDialogTarget,
      () => {
        this.cropperModalTarget.classList.replace("flex", "hidden");
        this.#destroyCropper();
        if (this.objectUrl) {
          URL.revokeObjectURL(this.objectUrl);
          this.objectUrl = null;
        }
        if (!this.element.classList.contains("is-open") && !this.#lightboxOpen()) {
          document.removeEventListener("keydown", this.handleKeydown);
        }
      },
    );
  }

  #playEnterAnimation(backdrop, dialog) {
    backdrop.classList.remove("turbo-modal-backdrop-enter", "turbo-modal-backdrop-leave");
    if (dialog) {
      dialog.classList.remove("turbo-modal-dialog-enter", "turbo-modal-dialog-leave");
    }
    void backdrop.offsetWidth;
    backdrop.classList.add("turbo-modal-backdrop-enter");
    if (dialog) dialog.classList.add("turbo-modal-dialog-enter");
  }

  #playLeaveAnimation(backdrop, dialog, onFinish) {
    const clearClasses = () => {
      backdrop.classList.remove("turbo-modal-backdrop-enter", "turbo-modal-backdrop-leave");
      if (dialog) {
        dialog.classList.remove("turbo-modal-dialog-enter", "turbo-modal-dialog-leave");
      }
    };

    if (
      !dialog ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      clearClasses();
      onFinish();
      return;
    }

    let finished = false;
    let ends = 0;

    const done = () => {
      if (finished) return;
      finished = true;
      window.clearTimeout(fallbackId);
      backdrop.removeEventListener("animationend", onAnimationEnd);
      clearClasses();
      onFinish();
    };

    const onAnimationEnd = (event) => {
      if (event.target !== backdrop && event.target !== dialog) return;
      ends += 1;
      if (ends >= 2) done();
    };

    backdrop.classList.replace("turbo-modal-backdrop-enter", "turbo-modal-backdrop-leave");
    dialog.classList.replace("turbo-modal-dialog-enter", "turbo-modal-dialog-leave");

    backdrop.addEventListener("animationend", onAnimationEnd);
    const fallbackId = window.setTimeout(done, this.constructor.LEAVE_FALLBACK_MS);
  }

  #destroyCropper() {
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }
  }

  #cropperOpen() {
    return (
      this.hasCropperModalTarget &&
      !this.cropperModalTarget.classList.contains("hidden")
    );
  }

  #lightboxOpen() {
    return (
      this.hasLightboxTarget && !this.lightboxTarget.classList.contains("hidden")
    );
  }

  #removeMenuListeners() {
    document.removeEventListener("click", this.closeMenuOnOutside);
    if (!this.#lightboxOpen() && !this.#cropperOpen()) {
      document.removeEventListener("keydown", this.handleKeydown);
    }
  }
}
